// Branded scan-report template — mirrors LabReportTemplateDekyi's
// narrative branch so labs and scans print with the exact same header
// band, patient block, brand ribbon, table style, signature block, and
// footnote. Only the copy differs (ribbon says "Diagnostic Imaging",
// signature roles are sonographer/radiologist/consultant, footnote is
// imaging-specific). The result: whether a clinician gets a lab report
// or a scan report, it feels like the same document family.
//
// Every scan renders as narrative — two columns (Parameter / Finding)
// plus an optional Impression block. Flags were removed from the scan
// setup because imaging is inherently descriptive; nothing here reads
// a Flag column.
//
// The popup window loads JsBarcode from a CDN to render the barcode at
// print time — no npm install required. If the CDN is unreachable the
// section gracefully degrades to a text representation.

const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

export const generateScanReportHTMLDekyi = (
  reportData,
  selectedPatient,
  userInfo,
  companyInfo,
  signatures = {},
  // Legacy signature — older callers passed a scanFormats map to
  // switch between narrative and parameter/flag layouts. Every scan
  // renders as narrative now, so the arg is accepted for backward
  // compatibility but no longer branches the layout.
  // eslint-disable-next-line no-unused-vars
  scanFormats = {}
) => {
  const company = (companyInfo && companyInfo.length > 0)
    ? companyInfo[0]
    : { name: 'Medical & Diagnostics Imaging Center', location: '', digital_add: '', phone: '', email: '', mmda: '' };

  const dateOfResult = Object.keys(reportData)[0] || '';
  const now = new Date();
  const registeredOn = dateOfResult || now.toLocaleDateString();
  const reportedOn   = now.toLocaleString();

  // Stable order number — falls back to a deterministic combination so
  // the barcode always renders something scannable even in the history
  // view where no explicit order id was generated.
  const orderNumber = signatures.orderNumber
    || selectedPatient?.order_number
    || selectedPatient?.scan_no
    || `${selectedPatient?.id || 'X'}-${(dateOfResult || '').replace(/[^0-9]/g, '') || Date.now()}`;

  const barcodeValue = String(orderNumber || selectedPatient?.id || 'NO-ID').slice(0, 40);

  // Performed-by defaults to whoever is logged in (the sonographer
  // running the machine). Verified/approved come from the print dialog.
  const performedBy    = signatures.performedBy    || userInfo?.name || userInfo?.user_name || userInfo?.username || '';
  const performedTitle = signatures.performedTitle || 'Sonographer';
  const verifiedBy     = signatures.verifiedBy     || '';
  const verifiedTitle  = signatures.verifiedTitle  || 'Radiologist';
  const approvedBy     = signatures.approvedBy     || '';
  const approvedTitle  = signatures.approvedTitle  || 'Senior Radiologist / Consultant';
  const todayStr       = now.toLocaleDateString();

  // Address string sourced from company profile — same fallback chain
  // as the lab template.
  const facilityAddress = [company.location, company.digital_add]
    .filter(Boolean).join(' · ');

  // One <div class="lab-block"> per scan category, ordered by date desc
  // then category name — mirrors the lab template's block name so both
  // reports share the same CSS.
  const scansHTML = Object.keys(reportData)
    .sort((a, b) => new Date(b) - new Date(a))
    .map((date) => Object.keys(reportData[date])
      .sort()
      .map((scanName) => {
        const rows = reportData[date][scanName];

        // Optional clinical impression — sits directly below the
        // section table when set, mirroring the lab template.
        const impressionText = (rows.find(r => r.impression && String(r.impression).trim()) || {}).impression || '';

        // CONCLUSION rows float to the bottom so the narrative reads
        // naturally: findings first, conclusion last. Same behaviour
        // as the lab template's non-narrative branch.
        // Parameters with no finding are left off the report entirely —
        // printing "CERVIX | -" for a section the sonographer never
        // examined reads as a negative finding rather than an omission.
        const reported = rows.filter(
          r => r.para && String(r.para).trim() && String(r.finding ?? '').trim()
        );
        const nonConclusion = reported.filter(r => !r.para.toUpperCase().includes('CONCLUSION'));
        const conclusions   = reported.filter(r =>  r.para.toUpperCase().includes('CONCLUSION'));
        const ordered = [...nonConclusion, ...conclusions];

        // Nothing reported for this scan at all — drop the block rather
        // than print an empty table under a scan heading.
        if (ordered.length === 0 && !impressionText) return '';

        const sectionRows = ordered.map((r) => `
          <tr>
            <td style="padding:8px 12px; border:1px solid #d9dee5; vertical-align:top; width:30%; font-weight:600;">${escapeHtml(r.para)}</td>
            <td style="padding:8px 12px; border:1px solid #d9dee5; white-space:pre-wrap;">${escapeHtml(r.finding)}</td>
          </tr>
        `).join('');

        return `
          <div class="lab-block" style="margin: 14px 16px;">
            <div class="lab-block-title">${escapeHtml(scanName.toUpperCase())}</div>
            <!-- 12.5px (was 11.5) — the findings are what the patient
                 actually reads, so they get the extra point. -->
            <table style="width:100%; border-collapse:collapse; font-size:12.5px; line-height:1.45; margin-bottom: 10px;">
              <thead>
                <tr style="background:#3a5fd9; color:white;">
                  <th style="padding:8px 12px; text-align:left; border:1px solid #2a4ec0;">Parameter</th>
                  <th style="padding:8px 12px; text-align:left; border:1px solid #2a4ec0;">Finding</th>
                </tr>
              </thead>
              <tbody>${sectionRows}</tbody>
            </table>
            ${impressionText ? `
              <div style="margin: 10px 0; padding: 6px 0; font-size: 12.5px;">
                <span style="font-weight:700; color:#144074;">Impression:</span> ${escapeHtml(impressionText)}
              </div>
            ` : ''}
          </div>
        `;
      }).join('')
    ).join('');

  // Every declaration below mirrors LabReportTemplateDekyi so both
  // reports print as one visual family. Copy only diverges in the
  // ribbon, table headers, footnote and signature roles.
  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Scan Report — ${escapeHtml(selectedPatient?.fullName || 'Patient')}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500&display=swap" rel="stylesheet">
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    /* Force the browser to render background colours on PRINT and PDF
       export — without this Chrome/Edge silently strip the header band
       and table row colours. */
    html, body, .page, .header-band, .results-table th, .results-table td,
    .report-ribbon, .lab-block-title {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    html, body {
      font-family: 'Inter', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
      font-size: 9.2pt;
      color: #0f172a;
      background: #f3f4f6;
      font-feature-settings: 'kern' 1, 'liga' 1, 'tnum' 1, 'ss01' 1;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      orphans: 3;
      widows: 3;
    }
    .screen-only {
      padding: 16px;
      text-align: center;
      background: #fff;
      border-bottom: 1px solid #e5e7eb;
    }
    .screen-only button {
      padding: 10px 26px; margin: 0 6px;
      border: none; border-radius: 6px;
      background: #144074; color: #ffffff;
      font-weight: 700; font-size: 10.8pt;
      letter-spacing: 0.4px;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(20,64,116,0.25);
    }
    .screen-only button.close-btn { background: #475569; box-shadow: 0 2px 6px rgba(71,85,105,0.25); }
    .screen-only button:hover { filter: brightness(1.05); }

    .page {
      width: 210mm;
      min-height: 297mm;
      margin: 16px auto;
      padding: 0;
      background: #fff;
      box-shadow: 0 6px 20px rgba(0,0,0,0.08);
      position: relative;
    }

    /* ── Branded header band ─────────────────────────────────────
       Same deep navy gradient + gold accent rule as the lab
       template, so a patient handed one lab and one scan report
       feels like they're from the same institution.  */
    .header-band {
      background: linear-gradient(180deg, #1e5593 0%, #144074 100%);
      color: #ffffff;
      padding: 12px 22px 13px 22px;
      display: grid;
      grid-template-columns: 56px 1fr 200px;
      gap: 16px;
      align-items: center;
      border-bottom: 3px solid #d4a017;
      box-shadow: 0 1px 0 rgba(0,0,0,0.08);
    }
    .header-band .logo {
      width: 52px; height: 52px;
      background: #ffffff;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      overflow: hidden;
      box-shadow: 0 1px 2px rgba(0,0,0,0.18);
      border: 2px solid rgba(255,255,255,0.92);
    }
    .header-band .logo img { width: 100%; height: 100%; object-fit: contain; }
    .header-band .logo-fallback {
      font-weight: 800; color: #144074; font-size: 17pt; letter-spacing: 1px;
    }
    .header-band .facility-name {
      font-size: 16pt;
      font-weight: 800;
      letter-spacing: 0.2px;
      line-height: 1.08;
      text-transform: uppercase;
      text-shadow: 0 1px 0 rgba(0,0,0,0.18);
    }
    .header-band .facility-tag {
      font-size: 9pt;
      font-weight: 600;
      color: #f8d97e;
      letter-spacing: 1.2px;
      text-transform: uppercase;
      margin-top: 4px;
    }
    .header-band .facility-meta {
      font-size: 8.4pt;
      color: rgba(255,255,255,0.92);
      margin-top: 6px;
      line-height: 1.45;
      letter-spacing: 0.2px;
    }
    .header-band .barcode-box {
      background: #ffffff;
      padding: 6px 8px 3px 8px;
      border-radius: 3px;
      text-align: center;
      color: #0f172a;
      box-shadow: 0 1px 2px rgba(0,0,0,0.18);
    }
    .header-band .barcode-box svg { width: 100%; height: 44px; display: block; }
    .header-band .barcode-fallback {
      font-family: 'JetBrains Mono', monospace;
      font-size: 10pt; letter-spacing: 1px;
      word-break: break-all;
    }
    .header-band .dates {
      font-size: 8.2pt;
      color: #ffffff;
      margin-top: 6px;
      text-align: right;
      line-height: 1.4;
    }
    .header-band .dates div { margin-top: 3px; }
    .header-band .dates strong {
      color: #ffffff;
      font-weight: 700;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      font-size: 8.2pt;
    }

    /* Report ribbon — sits just under the header band and reinforces
       what this document is. Same brand voice as the band. */
    .report-ribbon {
      background: #f2c33a;
      color: #144074;
      text-align: center;
      font-weight: 800;
      font-size: 8.8pt;
      letter-spacing: 3px;
      padding: 3px 0 3px 0;
      text-transform: uppercase;
    }

    /* ── Patient + order info block ───────────────────────────── */
    .id-block {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 18px;
      padding: 9px 24px 6px 24px;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
    }
    .id-block .field {
      font-size: 9pt;
      line-height: 1.4;
      color: #0f172a;
    }
    .id-block .field strong {
      display: inline-block;
      min-width: 120px;
      color: #1f2937;
      font-weight: 700;
      letter-spacing: 0.3px;
      text-transform: uppercase;
      font-size: 7.8pt;
    }
    .id-block .col-right { text-align: left; }
    .id-block .col-right .field strong { min-width: 95px; }

    /* ── Section / scan title ────────────────────────────────── */
    .lab-block { padding: 6px 24px 0 24px; }
    .lab-block-title {
      font-size: 10.5pt;
      font-weight: 800;
      color: #144074;
      text-transform: uppercase;
      letter-spacing: 1.4px;
      text-align: center;
      padding: 8px 0 4px 0;
      position: relative;
      margin-bottom: 4px;
      page-break-after: avoid;
      break-after: avoid;
    }
    .lab-block-title::after {
      content: '';
      display: block;
      width: 56px;
      height: 2px;
      background: #d4a017;
      margin: 4px auto 0 auto;
      border-radius: 2px;
    }

    /* ── End of report marker ────────────────────────────────── */
    .end-of-report {
      text-align: center;
      font-size: 8.6pt;
      color: #144074;
      margin: 14px 0 10px 0;
      letter-spacing: 4px;
      font-weight: 700;
      text-transform: uppercase;
    }

    /* ── Signature grid ──────────────────────────────────────── */
    .sig-block {
      padding: 0 24px 10px 24px;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .sig-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8.8pt;
    }
    .sig-table th {
      text-align: left;
      font-weight: 800;
      color: #144074;
      padding: 5px 7px 4px 7px;
      border-bottom: 2px solid #144074;
      font-size: 7.8pt;
      letter-spacing: 0.6px;
      text-transform: uppercase;
    }
    .sig-table th.sig-line,
    .sig-table th.date-col { text-align: left; }
    .sig-table td {
      padding: 13px 7px 5px 7px;
      border-bottom: 1px solid #94a3b8;
      vertical-align: top;
      font-size: 8.6pt;
      line-height: 1.35;
    }
    .sig-table td.role-cell { font-weight: 800; color: #144074; }
    .sig-table td.name-cell { color: #111827; font-weight: 600; }
    .sig-table td .title-line {
      display: block;
      color: #6b7280;
      font-size: 7.8pt;
      margin-top: 2px;
      font-weight: 500;
      letter-spacing: 0.2px;
    }

    /* ── Footnote ─────────────────────────────────────────────── */
    .footnote {
      padding: 7px 24px 14px 24px;
      font-size: 7.8pt;
      color: #475569;
      border-top: 2px solid #144074;
      line-height: 1.4;
    }
    .footnote strong {
      color: #144074;
      font-weight: 800;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      font-size: 7.4pt;
    }

    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      html, body { background: #fff; }
      .screen-only { display: none !important; }
      .page {
        margin: 0; box-shadow: none;
        width: 210mm; min-height: 297mm;
        page-break-after: always;
      }
      /* Gradient sometimes drops in print — keep a solid fallback so
         the band always carries colour. */
      .header-band {
        background: #144074 !important;
        background-image: linear-gradient(180deg, #1e5593 0%, #144074 100%) !important;
        color: #ffffff !important;
        border-bottom: 3px solid #d4a017 !important;
      }
      .report-ribbon { background: #f2c33a !important; color: #144074 !important; }
      /* Keep small atomic blocks together (patient-id, signature). */
      .sig-block, .id-block, .lab-block {
        page-break-inside: avoid !important;
        break-inside: avoid-page !important;
      }
      .lab-block-title {
        page-break-after: avoid !important;
        break-after: avoid !important;
      }
    }
  </style>
</head>
<body>
  <div class="screen-only">
    <button onclick="window.print()">Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">Close</button>
  </div>

  <div class="page">
    <!-- Navy header band -->
    <div class="header-band">
      <div class="logo">
        ${company.logo
          ? `<img src="${escapeHtml(company.logo)}" alt="logo" />`
          : `<div class="logo-fallback">${escapeHtml((company.name || 'S')[0])}</div>`}
      </div>
      <div>
        <div class="facility-name">${escapeHtml(company.name || 'Facility Name')}</div>
        ${company.mmda ? `<div class="facility-tag">${escapeHtml(company.mmda)}</div>` : ''}
        <div class="facility-meta">
          ${facilityAddress ? escapeHtml(facilityAddress) : ''}<br/>
          ${company.phone ? '☎ ' + escapeHtml(company.phone) : ''}${company.phone && company.email ? ' · ' : ''}${company.email ? '✉ ' + escapeHtml(company.email) : ''}
        </div>
      </div>
      <div>
        <div class="barcode-box">
          <svg id="scan-barcode"></svg>
          <div id="barcode-fallback" class="barcode-fallback" style="display:none;">${escapeHtml(barcodeValue)}</div>
        </div>
        <div class="dates">
          <div><strong>Registered:</strong> ${escapeHtml(registeredOn)}</div>
          <div><strong>Reported:</strong> ${escapeHtml(reportedOn)}</div>
        </div>
      </div>
    </div>

    <!-- Brand ribbon — reinforces what this document is. -->
    <div class="report-ribbon">· Diagnostic Imaging Report ·</div>

    <!-- Patient + order info -->
    <div class="id-block">
      <div class="col-left">
        <div class="field"><strong>Patient name:</strong> ${escapeHtml(selectedPatient?.fullName || '-')}</div>
        <div class="field"><strong>Age:</strong> ${escapeHtml(selectedPatient?.age || '-')}</div>
        <div class="field"><strong>Sex:</strong> ${escapeHtml(selectedPatient?.gender || '-')}</div>
        <div class="field"><strong>Modality:</strong> ${escapeHtml(signatures.modality || selectedPatient?.modality || 'Ultrasound')}</div>
        <div class="field"><strong>Referring Doctor:</strong> ${escapeHtml(signatures.referringDoctor || selectedPatient?.referring_doctor || '-')}</div>
      </div>
      <div class="col-right">
        <div class="field"><strong>Order number:</strong> ${escapeHtml(orderNumber)}</div>
        <div class="field"><strong>Phone:</strong> ${escapeHtml(selectedPatient?.tel || selectedPatient?.phone || company.phone || '-')}</div>
        <!-- Patient's own locality (patient.res), not the facility address.
             The facility's details already sit in the letterhead above, so
             repeating them here told the reader nothing about the patient. -->
        <div class="field"><strong>Location:</strong> ${escapeHtml(selectedPatient?.res || selectedPatient?.location || selectedPatient?.address || '-')}</div>
        <div class="field"><strong>Patient ID:</strong> ${escapeHtml(selectedPatient?.id || '-')}</div>
      </div>
    </div>

    <!-- Result tables -->
    ${scansHTML}

    <!-- End of report -->
    <div class="end-of-report">***** End of Report *****</div>

    <!-- Signature block -->
    <div class="sig-block">
      <table class="sig-table">
        <thead>
          <tr>
            <th style="width:22%;">Role</th>
            <th style="width:28%;">Name</th>
            <th class="sig-line" style="width:30%;">Signature</th>
            <th class="date-col" style="width:20%;">Date</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="role-cell">Performed by:<br/><span class="title-line">${escapeHtml(performedTitle)}</span></td>
            <td class="name-cell">${escapeHtml(performedBy || '-')}</td>
            <td></td>
            <td>${escapeHtml(todayStr)}</td>
          </tr>
          <tr>
            <td class="role-cell">Verified by:<br/><span class="title-line">${escapeHtml(verifiedTitle)}</span></td>
            <td class="name-cell">${escapeHtml(verifiedBy || '-')}</td>
            <td></td>
            <td>${verifiedBy ? escapeHtml(todayStr) : ''}</td>
          </tr>
          <tr>
            <td class="role-cell">Approved by:<br/><span class="title-line">${escapeHtml(approvedTitle)}</span></td>
            <td class="name-cell">${escapeHtml(approvedBy || '-')}</td>
            <td></td>
            <td>${approvedBy ? escapeHtml(todayStr) : ''}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="footnote">
      <strong>Important Notes —</strong>
      Findings relate only to the images acquired at the time of the study. Clinical correlation is recommended.
      This report is confidential and intended solely for the named patient and the referring clinician.
      Unauthorized reproduction or distribution is prohibited.
      <strong>&nbsp;·&nbsp;For Medical Use Only</strong>
    </div>
  </div>

  <script>
    // Render the barcode once JsBarcode finishes loading. If the CDN is
    // unreachable (offline print), fall back to a textual representation.
    (function () {
      function renderBarcode() {
        try {
          if (typeof JsBarcode !== 'function') throw new Error('no jsbarcode');
          JsBarcode('#scan-barcode', ${JSON.stringify(barcodeValue)}, {
            format: 'CODE128',
            displayValue: true,
            fontSize: 11,
            height: 38,
            margin: 0
          });
        } catch (e) {
          var svg = document.getElementById('scan-barcode');
          var fb  = document.getElementById('barcode-fallback');
          if (svg) svg.style.display = 'none';
          if (fb)  fb.style.display  = 'block';
        }
      }
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        setTimeout(renderBarcode, 0);
      } else {
        document.addEventListener('DOMContentLoaded', renderBarcode);
      }
    })();
  </script>
</body>
</html>`;

  return html;
};
